import React from 'react';
import styled from 'styled-components';

// Styled Components
const ToggleRowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: ${props => props.theme.background};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.muted};
  }
`;

const ToggleContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToggleTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.foreground};
  margin-bottom: 2px;
`;

const ToggleDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.mutedForeground};
  line-height: 1.4;
`;

const ToggleSwitch = styled.button<{ $checked: boolean }>`
  position: relative;
  display: inline-flex;
  height: 24px;
  width: 44px;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: ${props => props.$checked ? '#3b82f6' : '#e5e7eb'};
  transition: all 0.15s ease;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.primary};
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.$checked ? '#2563eb' : '#d1d5db'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ToggleThumb = styled.span<{ $checked: boolean }>`
  position: absolute;
  top: 1px;
  left: 1px;
  height: 20px;
  width: 20px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transform: ${props => props.$checked ? 'translateX(20px)' : 'translateX(0)'};
  transition: transform 0.15s ease;
  pointer-events: none;
`;

// Types
interface ToggleRowProps {
  title: string;
  description?: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}

// Component
export const ToggleRow: React.FC<ToggleRowProps> = ({ title, description, checked, onChange, disabled = false }) => {
  return (
    <ToggleRowContainer style={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <ToggleContent>
        <ToggleTitle>{title}</ToggleTitle>
        {description && <ToggleDescription>{description}</ToggleDescription>}
      </ToggleContent>
      <ToggleSwitch
        $checked={checked}
        onClick={disabled ? undefined : onChange}
        role="switch"
        aria-checked={checked}
        aria-label={`Toggle ${title}`}
        aria-disabled={disabled}
        disabled={disabled}
      >
        <ToggleThumb $checked={checked} />
      </ToggleSwitch>
    </ToggleRowContainer>
  );
};
